import { request } from '@/shared/api/request'
import { camelize } from '@/shared/lib/camelize'

export interface ConversationParticipant {
  id: string
  username: string
  displayName: string
  avatarUrl?: string | null
}

export interface Conversation {
  id: string
  lastMessageText?: string | null
  lastMessageAt?: string | null
  lastSenderId?: string | null
  unreadCount: number
  participants: ConversationParticipant[]
}

export interface ChatMessage {
  id: string
  text?: string | null
  /** Server's exact timestamp string - echoed back as the pagination cursor. */
  createdAt: string
  senderId: string
  senderUsername: string
  senderAvatar?: string | null
  isOwn: boolean
}

/** The person on the other side (participants include the current user). */
export function otherParticipant(
  conversation: Conversation,
  myUserId: string | null | undefined,
): ConversationParticipant | undefined {
  const me = myUserId?.toLowerCase()
  return (
    conversation.participants.find((participant) => participant.id.toLowerCase() !== me) ??
    conversation.participants[0]
  )
}

export function participantLabel(participant: ConversationParticipant | undefined): string {
  if (!participant) return ''
  return participant.displayName || participant.username
}

const BASE = '/api/messaging'

/** A cursor far in the future - with reverse=true this yields the latest messages. */
export const FAR_FUTURE_DATE = '2100-01-01T00:00:00Z'

export const messagingApi = {
  async getConversations(limit = 100, offset = 0): Promise<Conversation[]> {
    const data = await request<unknown>({
      url: `${BASE}/conversations`,
      params: { limit, offset },
    })
    return camelize<Conversation[]>(data)
  },

  /** Returns the conversation id (existing or newly created) with `userId`. */
  async startConversation(userId: string): Promise<string> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${BASE}/conversations`,
      data: { user_id: userId },
    })
    const id = data?.id ?? data?.conversation_id
    if (typeof id !== 'string') throw new Error('Missing conversation id in response')
    return id
  },

  async leaveConversation(id: string): Promise<void> {
    await request({ method: 'POST', url: `${BASE}/conversations/${id}/leave` })
  },

  /**
   * Backend pagination semantics: reverse=true returns messages *older* than
   * `date` (newest first), reverse=false returns messages *newer* than `date`
   * (oldest first).
   */
  async getMessages(
    conversationId: string,
    date: string,
    reverse: boolean,
    limit = 50,
  ): Promise<ChatMessage[]> {
    const data = await request<unknown>({
      url: `${BASE}/conversations/${conversationId}/messages`,
      params: { date, reverse: String(reverse), limit },
    })
    return camelize<ChatMessage[]>(data)
  },

  /** Returns the new message's server id. */
  async sendMessage(conversationId: string, text: string): Promise<string> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${BASE}/conversations/${conversationId}/messages`,
      data: { text },
    })
    if (typeof data?.id !== 'string') throw new Error('Missing message id in response')
    return data.id
  },

  /**
   * Single message with its authoritative server timestamp - replaces the
   * optimistic echo so pagination cursors never rely on the device clock.
   */
  async getMessage(id: string): Promise<ChatMessage> {
    const data = await request<unknown>({ url: `${BASE}/messages/${id}` })
    return camelize<ChatMessage>(data)
  },

  async deleteMessage(id: string): Promise<void> {
    await request({ method: 'DELETE', url: `${BASE}/messages/${id}` })
  },

  /** Marks everything up to `date` (defaults to now) read; best-effort. */
  async markRead(conversationId: string, date?: string): Promise<void> {
    try {
      await request({
        method: 'PUT',
        url: `${BASE}/messages/read`,
        data: { conversation_id: conversationId, date: date ?? new Date().toISOString() },
      })
    } catch {
      /* best-effort */
    }
  },

  async getUnreadCount(): Promise<number> {
    try {
      const data = await request<Record<string, unknown>>({ url: `${BASE}/unread-count` })
      const value = data?.count ?? data?.unread_count ?? data?.unreadCount
      return typeof value === 'number' ? value : 0
    } catch {
      return 0
    }
  },
}
